/**
 * SmartPhrase shortcut expansion for the Note editor.
 * Typing `.NAME` + Space/Enter replaces the shortcut with stored content.
 * Canonical Placeholder Vocabulary tokens are resolved from the current
 * Encounter context at insert time. Inserted text is ordinary Note content.
 */
import {
  deleteCharsBeforeCursor,
  insertHtmlAtCursor,
  plaintextToEditorHtml,
  serializeNoteRtf,
  textBeforeCursorForShortcut,
} from "@/lib/noteRtf";
import { ageFromBirthDate, patientFullName } from "@/lib/patientChart";
import {
  resolveCanonicalPlaceholders,
  normalizePlaceholderContext,
} from "@/lib/placeholderResolve";
import { listSmartPhrases, plainTextFromContent } from "@/lib/smartPhraseApi";

/** @deprecated Legacy exact tokens; kept for tests and alias documentation. */
export const SMART_PHRASE_PATIENT_NAME_TOKEN = "@PatientName@";
export const SMART_PHRASE_AGE_TOKEN = "@age@";

/** Name and current age from the FHIR patient on the current Encounter. */
export function smartPhrasePatientValues(patient) {
  const age = ageFromBirthDate(patient?.birthDate);
  return {
    patientName: patientFullName(patient) || "",
    age: age == null ? "" : String(age),
  };
}

/**
 * Resolve canonical placeholders (plus documented aliases) from Encounter context.
 * Missing known values become empty strings. Unknown / reserved tokens stay.
 */
export function resolveSmartPhraseVariables(content, context = {}) {
  return resolveCanonicalPlaceholders(content, normalizePlaceholderContext(context));
}

/** Plain text to insert: stored content with canonical tokens resolved. */
export function smartPhraseNoteInsertion(phrase, context) {
  return resolveSmartPhraseVariables(smartPhraseContentForNote(phrase), context);
}

/**
 * Match `.SHORTCUT` immediately before the cursor.
 * Requires start-of-text or whitespace before the leading `.` so values like
 * `file.txt` are not treated as SmartPhrase shortcuts.
 */
export function detectSmartPhraseShortcut(value, cursor) {
  const text = String(value || "");
  const pos = Number(cursor);
  if (!Number.isFinite(pos) || pos < 2) return null;
  const before = text.slice(0, pos);
  const match = before.match(/(?:^|[\s])(\.([A-Za-z0-9][A-Za-z0-9_-]*))$/);
  if (!match) return null;
  const token = match[1];
  return {
    shortcut: match[2],
    start: pos - token.length,
    end: pos,
    token,
  };
}

/** Plain text to insert into the existing textarea Note editor. */
export function smartPhraseContentForNote(phrase) {
  if (!phrase) return "";
  return plainTextFromContent(phrase.content, phrase.format);
}

/** Stored SmartPhrase body with tags kept so the RTF Note editor can insert rich text. */
export function smartPhraseRichContentForNote(phrase) {
  if (!phrase) return "";
  return String(phrase.content || "");
}

/**
 * Resolved SmartPhrase HTML for the RTF Note editor.
 * Placeholder resolution stays in the resolver; this only formats the result.
 */
export function smartPhraseHtmlForNote(phrase, context) {
  const resolved = resolveSmartPhraseVariables(smartPhraseRichContentForNote(phrase), context);
  if (!phrase || phrase.format === "plaintext") {
    return plaintextToEditorHtml(resolved);
  }
  return serializeNoteRtf(resolved) || plaintextToEditorHtml(resolved);
}

/**
 * Replace the detected `.SHORTCUT` at cursor with content.
 * Space → trailing space; Enter → trailing newline.
 */
export function expandSmartPhraseAtCursor(value, cursor, content, triggerKey) {
  const detected = detectSmartPhraseShortcut(value, cursor);
  if (!detected) return null;
  const suffix = triggerKey === "Enter" ? "\n" : " ";
  const insertion = `${content}${suffix}`;
  const next =
    String(value || "").slice(0, detected.start) +
    insertion +
    String(value || "").slice(detected.end);
  return {
    next,
    cursor: detected.start + insertion.length,
    shortcut: detected.shortcut,
  };
}

/** Exact active SmartPhrase by name/shortcut (case-insensitive). */
export async function findActiveSmartPhraseByName(name) {
  const needle = String(name || "").trim();
  if (!needle) return null;
  const data = await listSmartPhrases({
    q: needle,
    active: true,
    owner: "all",
  });
  const upper = needle.toUpperCase();
  return (
    (data.items || []).find((row) => String(row.name || "").toUpperCase() === upper) || null
  );
}

/**
 * Handle Space/Enter in a textarea: expand `.SHORTCUT` when an active
 * SmartPhrase matches; otherwise insert the trigger character.
 * Returns true when the event was handled (caller should preventDefault).
 */
export async function tryExpandSmartPhraseShortcut({
  event,
  textarea,
  value,
  onChange,
  restoreSelection,
  patient,
  context,
}) {
  if (!event || !textarea || typeof onChange !== "function") return false;
  if (event.key !== " " && event.key !== "Enter") return false;
  if (event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return false;
  if (event.isComposing) return false;

  const start = textarea.selectionStart ?? 0;
  const end = textarea.selectionEnd ?? start;
  if (start !== end) return false;

  const detected = detectSmartPhraseShortcut(value, start);
  if (!detected) return false;

  event.preventDefault();

  const insertContext = context || (patient ? { patient } : {});

  try {
    const phrase = await findActiveSmartPhraseByName(detected.shortcut);
    if (!phrase) {
      const ch = event.key === "Enter" ? "\n" : " ";
      const next = String(value || "").slice(0, start) + ch + String(value || "").slice(end);
      onChange(next);
      restoreSelection?.(textarea, start + ch.length);
      return true;
    }
    const content = smartPhraseNoteInsertion(phrase, { ...insertContext, now: insertContext.now || new Date() });
    const result = expandSmartPhraseAtCursor(value, start, content, event.key);
    if (!result) return true;
    onChange(result.next);
    restoreSelection?.(textarea, result.cursor);
    return true;
  } catch {
    const ch = event.key === "Enter" ? "\n" : " ";
    const next = String(value || "").slice(0, start) + ch + String(value || "").slice(end);
    onChange(next);
    restoreSelection?.(textarea, start + ch.length);
    return true;
  }
}

function emitEditorHtml(editor, onChange) {
  onChange(serializeNoteRtf(editor.innerHTML || ""));
}

/**
 * Handle Space/Enter in the RTF Note editor: expand `.SHORTCUT` at the cursor
 * with already-resolved SmartPhrase content. Does not replace the whole note.
 */
export async function tryExpandSmartPhraseShortcutInRtf({
  event,
  editor,
  onChange,
  patient,
  context,
}) {
  if (!event || !editor || typeof onChange !== "function") return false;
  if (event.key !== " " && event.key !== "Enter") return false;
  if (event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return false;
  if (event.isComposing) return false;

  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return false;
  const anchor = sel.anchorNode;
  if (anchor !== editor && !editor.contains(anchor)) return false;

  const before = textBeforeCursorForShortcut(editor);
  const detected = detectSmartPhraseShortcut(before, before.length);
  if (!detected) return false;

  event.preventDefault();
  const insertContext = context || (patient ? { patient } : {});
  const suffix = event.key === "Enter" ? "<br>" : " ";

  try {
    const phrase = await findActiveSmartPhraseByName(detected.shortcut);
    if (!phrase) {
      insertHtmlAtCursor(editor, suffix);
      emitEditorHtml(editor, onChange);
      return true;
    }
    const html = smartPhraseHtmlForNote(phrase, {
      ...insertContext,
      now: insertContext.now || new Date(),
    });
    deleteCharsBeforeCursor(editor, detected.token.length);
    insertHtmlAtCursor(editor, `${html}${suffix}`);
    emitEditorHtml(editor, onChange);
    return true;
  } catch {
    insertHtmlAtCursor(editor, suffix);
    emitEditorHtml(editor, onChange);
    return true;
  }
}
